import { Component, Element, Event, EventEmitter, JSX, Prop, h } from '@stencil/core'

import { KeyCode } from '../../enums/keycode.enum'
import { isUndefined } from '../../utils/helpers'

@Component({
  tag: 'air-modal',
  styleUrl: 'air-modal.scss',
  shadow: true,
})
export class AirModal {
  /** Reference to the host element */
  @Element() host!: HTMLAirModalElement

  /** Specifies what icon from asset library is shown for closing the modal */
  @Prop() iconClose = 'x'

  /** Specifies if the close icon is shown */
  @Prop() dismissible = false

  /** Specifies if the modal prevent the closing by clicking outside or pressing escape  */
  @Prop() persistent = false

  /** Defines the open state */
  @Prop({ mutable: true, reflect: true }) opened = false

  /** Emits the air-close event */
  @Event({ eventName: 'air-close' }) close!: EventEmitter<void>

  private headerSlotHasContent = false
  private footerSlotHasContent = false

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.code === KeyCode.Escape) {
      this.handleClose()
    }
  }

  /** Handle closing the modal by clicking outside or pressing Escape */
  private handleClose = (): void => {
    if (this.persistent) return
    this.closeModal()
  }

  private closeModal = (): void => {
    const event = this.close.emit()

    if (event.defaultPrevented) {
      return
    }
    this.opened = false
  }

  private renderCloseIcon(): JSX.Element | null {
    if (isUndefined(this.iconClose) || this.iconClose === '') {
      return <air-icon name="x"></air-icon>
    }
    return <air-icon name={this.iconClose}></air-icon>
  }

  componentWillLoad(): void {
    this.headerSlotHasContent = this.slotHasChildNodes('header')
    this.footerSlotHasContent = this.slotHasChildNodes('footer')
  }

  private slotHasChildNodes(slotName: string): boolean {
    return this.host.querySelector(`[slot="${slotName}"]`)?.hasChildNodes() || false
  }

  render(): JSX.Element {
    const { dismissible, opened } = this
    const modalClasses = {
      [`modal`]: true,
      [`is-visible`]: opened,
    }

    return (
      <div onKeyDown={this.handleKeyDown} aria-modal="true" tabindex="-1" class={modalClasses}>
        <div onClick={this.handleClose} class="modal-overlay"></div>
        <div class="modal-content">
          {(this.headerSlotHasContent || dismissible) && (
            <header class="modal-header">
              <div class="header-content">
                <slot name="header"></slot>
              </div>
              {dismissible && (
                <div role="button" class="modal-close" aria-label="close" onClick={this.closeModal}>
                  {this.renderCloseIcon()}
                </div>
              )}
            </header>
          )}
          <div class="modal-body">
            <slot />
          </div>
          {this.footerSlotHasContent && (
            <footer class="modal-footer">
              <slot name="footer"></slot>
            </footer>
          )}
        </div>
      </div>
    )
  }
}
